
import { Button } from "@/components/ui/button";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { Sidebar } from "./Sidebar";
import { Menu } from "lucide-react";
import { HeaderCreditsDisplay } from "@/components/credits/HeaderCreditsDisplay";

export const Navigation = ({ 
  activeView, 
  setActiveView,
  showRoles,
  setShowRoles 
}: { 
  activeView: string; 
  setActiveView: (view: string) => void;
  showRoles: boolean;
  setShowRoles: (show: boolean) => void;
}) => {
  const { toast } = useToast();

  const handleViewChange = (view: string) => {
    setActiveView(view);
    localStorage.setItem('lastActiveView', view);
    
    toast({
      title: `Switched to ${view === 'chat' ? 'Stewie Chat' : 'Stewie Trader'}`,
      description: `You are now using ${view === 'chat' ? 'Stewie Chat' : 'Stewie Trader'}.`,
      variant: "info",
    });
  };

  const handleLogout = async () => {
    try {
      localStorage.clear();
      const { error } = await supabase.auth.signOut();
      window.location.href = '/auth';
      
      if (error && !error.message?.includes('session_not_found')) {
        toast({
          title: "Warning",
          description: "There was an issue during logout, but you have been signed out successfully.",
          variant: "warning",
        });
        console.error('Logout error:', error);
      } else {
        toast({
          title: "Logged out successfully",
          description: "You have been logged out of your account.",
          variant: "success",
        });
      }
    } catch (error: any) {
      console.error('Logout error:', error);
      window.location.href = '/auth';
      toast({
        title: "Warning",
        description: "There was an issue during logout, but you have been signed out successfully.",
        variant: "warning",
      });
    }
  };

  return (
    <nav className="bg-white shadow-sm w-full">
      <div className="max-w-[2000px] mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between h-16">
          <div className="flex">
            <div className="flex-shrink-0 flex items-center">
              <Sheet>
                <SheetTrigger asChild>
                  <button className="flex items-center gap-2 hover:opacity-80 transition-opacity">
                    <Menu className="h-5 w-5 md:hidden" />
                    <div className="relative h-10 w-10">
                      <img
                        src="/lovable-uploads/e3b5de11-4d35-4574-a2a6-3b0df6dc385d.png"
                        alt="Stewie Trader"
                        className="h-full w-full object-contain rounded-full"
                      />
                    </div>
                  </button>
                </SheetTrigger>
                <SheetContent side="left" className="p-0 bg-transparent border-none">
                  <div className="h-full w-64 bg-white shadow-lg flex flex-col overflow-y-auto">
                    <div className="flex flex-col space-y-3 p-4 border-b">
                      <div className="flex items-center justify-around">
                        <button
                          onClick={() => handleViewChange('chat')}
                          className={
                            `${activeView === 'chat' ? 'text-primary font-semibold' : 'text-gray-700'} text-sm`
                          }
                        >
                          Stewie Chat
                        </button>
                        <button
                          onClick={() => handleViewChange('trader')}
                          className={
                            `${activeView === 'trader' ? 'text-primary font-semibold' : 'text-gray-700'} text-sm`
                          }
                        >
                          Stewie Trader
                        </button>
                      </div>
                      
                      {/* Mobile Credits Display */}
                      <div className="flex justify-center pt-2">
                        <HeaderCreditsDisplay />
                      </div>
                    </div>
                    <Sidebar />
                  </div>
                </SheetContent>
              </Sheet>
            </div>
            <div className="hidden sm:ml-6 sm:flex sm:space-x-8">
              <button
                onClick={() => handleViewChange('chat')}
                className={`${
                  activeView === 'chat'
                    ? 'border-primary text-gray-900'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                } inline-flex items-center px-1 pt-1 border-b-2 text-sm font-medium`}
              >
                Stewie Chat
              </button>
              <button
                onClick={() => handleViewChange('trader')}
                className={`${
                  activeView === 'trader'
                    ? 'border-primary text-gray-900'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                } inline-flex items-center px-1 pt-1 border-b-2 text-sm font-medium`}
              >
                Stewie Trader
              </button>
            </div>
          </div>
          <div className="flex items-center space-x-4">
            {/* Credits Display */}
            <HeaderCreditsDisplay />
            
            <Button
              variant="ghost"
              onClick={() => {
                setShowRoles(!showRoles);
                if (!showRoles) {
                  toast({
                    title: "AI Roles Panel Opened",
                    description: "Select an AI role to assist you with specific tasks.",
                    variant: "info",
                  });
                }
              }}
              className="rounded-[4px]"
            >
              AI Roles
            </Button>
            <Button
              variant="outline"
              onClick={handleLogout}
              className="rounded-[4px] border-gray-200 hover:bg-gray-50"
            >
              Logout
            </Button>
          </div>
        </div>
      </div>
    </nav>
  );
};
